import React, { useEffect, useState, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { Check, X, MessageCircle, Search, Copy, Send, Edit3, FileDown, Trash2 } from "lucide-react";
import api from "../../lib/api";
import { useApp } from "../../context/AppContext";
import { toast } from "sonner";
import { downloadReservationPdf } from "../../lib/pdf";

const REJECT_REASONS = [
    { value: "sold_out", label: "Seçilen tarihlerde oda kalmadı" },
    { value: "dates_unavailable", label: "Belirtilen tarihler müsait değil" },
    { value: "company_terms", label: "Toplu rezervasyon koşulları uygun değil" },
    { value: "incomplete_info", label: "Eksik / hatalı bilgi" },
    { value: "other", label: "Diğer (manuel mesaj)" },
];

const fillTemplate = (tpl, vars) => {
    let out = tpl || "";
    Object.entries(vars).forEach(([k, v]) => {
        out = out.split(`{${k}}`).join(v ?? "");
    });
    return out;
};

const reservationVars = (r, settings, reason = "") => ({
    name: r.customer_name,
    code: r.reservation_code,
    hotel: r.hotel_name,
    check_in: r.check_in,
    check_out: r.check_out,
    nights: r.nights,
    rooms: `${r.room_name} × ${r.rooms_count}`,
    guests: r.guests,
    total: `${r.total_price} SAR`,
    deposit: `${r.deposit_amount} SAR`,
    remaining: `${Math.max(0, (r.total_price || 0) - (r.deposit_amount || 0))} SAR`,
    profile_url: `${window.location.origin}/profile`,
    iban: settings.iban || "-",
    bank: settings.bank_name || "-",
    account_holder: settings.account_holder || "-",
    company: settings.company_name || "Asis Hotels",
    reason,
});

const DEFAULT_APPROVE_TEMPLATE =
`Merhaba {name},
Rezervasyon talebiniz ONAYLANDI. ✅

Rezervasyon Kodu: {code}
Otel: {hotel}
Oda: {rooms}
Tarih: {check_in} → {check_out} ({nights} gece)
Misafir: {guests} kişi
Toplam: {total}
%10 Ön Ödeme: {deposit}
🏨 Otele girişte ödenecek kalan tutar: {remaining}

Ödeme bilgileri:
Banka: {bank}
IBAN: {iban}
Hesap Sahibi: {account_holder}
Açıklama: {code}

📄 Rezervasyon makbuzunuzu (PDF) üye girişi yaparak Hesabım > Rezervasyonlarım bölümünden indirebilirsiniz:
{profile_url}

Ön ödemenizi gerçekleştirip dekontu bu numaraya iletmeniz halinde rezervasyonunuz kesinleşecektir.
— {company}`;

const DEFAULT_REJECT_TEMPLATE =
`Merhaba {name},
Maalesef {code} numaralı rezervasyon talebiniz şu sebeple onaylanamadı:

{reason}

Anlayışınız için teşekkür ederiz. Alternatif tarih veya otel için bize WhatsApp üzerinden ulaşabilirsiniz.
— {company}`;

const buildApproveMessage = (r, settings) =>
    fillTemplate(settings.wa_template_approved || DEFAULT_APPROVE_TEMPLATE, reservationVars(r, settings));

const buildRejectMessage = (r, settings, reason) =>
    fillTemplate(settings.wa_template_rejected || DEFAULT_REJECT_TEMPLATE, reservationVars(r, settings, reason));

const ActionModal = ({ reservation, action, settings, onClose, onConfirm }) => {
    const isApprove = action === "approved";
    const [reason, setReason] = useState("sold_out");
    const [customMsg, setCustomMsg] = useState("");
    const [edited, setEdited] = useState(false);
    const [messageText, setMessageText] = useState(
        isApprove ? buildApproveMessage(reservation, settings) : buildRejectMessage(reservation, settings, REJECT_REASONS[0].label),
    );

    // Update message when reason changes (only if not edited manually)
    useEffect(() => {
        if (isApprove || edited) return;
        const reasonLabel = reason === "other"
            ? (customMsg.trim() || "Detaylı bilgi için sizinle iletişime geçeceğiz.")
            : REJECT_REASONS.find((r) => r.value === reason)?.label || "";
        setMessageText(buildRejectMessage(reservation, settings, reasonLabel));
    }, [reason, customMsg, edited, isApprove, reservation, settings]);

    const phoneDigits = (reservation.customer_phone || "").replace(/\D/g, "");
    const waUrl = `https://wa.me/${phoneDigits}?text=${encodeURIComponent(messageText)}`;

    const sendAndConfirm = async () => {
        // Open WhatsApp link in new tab, then update status
        window.open(waUrl, "_blank");
        await onConfirm({
            status: action,
            rejection_reason: !isApprove ? messageText : "",
            admin_note: messageText,
        });
    };

    return (
        <div className="fixed inset-0 z-50 bg-emerald_deep/85 backdrop-blur-sm flex items-center justify-center p-4">
            <div className="bg-emerald_surface border border-gold/50 w-full max-w-2xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
                <div className="p-6 border-b border-border_green flex items-center justify-between">
                    <div>
                        <div className={`text-xs uppercase tracking-[0.25em] mb-1 ${isApprove ? "text-emerald-400" : "text-red-400"}`}>
                            {isApprove ? "✓ Onaylama" : "✗ Reddetme"}
                        </div>
                        <h2 className="font-serif text-2xl">{reservation.customer_name}</h2>
                        <div className="text-xs text-muted_green mt-1 font-mono">{reservation.reservation_code} · {reservation.hotel_name}</div>
                    </div>
                    <button onClick={onClose} className="text-muted_green hover:text-bone" data-testid="modal-close">
                        <X className="w-5 h-5" />
                    </button>
                </div>

                <div className="p-6 space-y-5">
                    {!isApprove && (
                        <div>
                            <label className="label-dark">Red Sebebi</label>
                            <select
                                value={reason}
                                onChange={(e) => { setReason(e.target.value); setEdited(false); }}
                                className="input-dark"
                                data-testid="reject-reason-select"
                            >
                                {REJECT_REASONS.map((r) => (
                                    <option key={r.value} value={r.value} className="bg-emerald_surface">{r.label}</option>
                                ))}
                            </select>
                            {reason === "other" && (
                                <div className="mt-3">
                                    <label className="label-dark">Manuel Sebep (müşteriye gösterilecek)</label>
                                    <textarea
                                        rows={2}
                                        value={customMsg}
                                        onChange={(e) => { setCustomMsg(e.target.value); setEdited(false); }}
                                        placeholder="Örn: Belirttiğiniz tarihlerde dolu, 15 Eylül sonrası için müsaitiz."
                                        className="input-dark"
                                        data-testid="reject-custom-input"
                                    />
                                </div>
                            )}
                        </div>
                    )}

                    <div>
                        <div className="flex items-center justify-between mb-2">
                            <label className="label-dark !mb-0">WhatsApp Mesajı</label>
                            <button
                                onClick={() => setEdited(!edited)}
                                className="text-xs text-gold hover:underline flex items-center gap-1"
                            >
                                <Edit3 className="w-3 h-3" /> {edited ? "Otomatik" : "Düzenle"}
                            </button>
                        </div>
                        <textarea
                            rows={12}
                            value={messageText}
                            onChange={(e) => { setMessageText(e.target.value); setEdited(true); }}
                            disabled={!edited && isApprove}
                            className="input-dark font-mono text-xs"
                            data-testid="message-textarea"
                        />
                        <div className="text-[10px] text-muted_green mt-1">
                            Bu mesaj WhatsApp ile <span className="text-gold">{reservation.customer_phone}</span> numarasına gönderilecek.
                            {!edited && isApprove && " 'Düzenle'ye tıklayarak değiştirebilirsiniz."}
                        </div>
                    </div>
                </div>

                <div className="p-6 border-t border-border_green flex items-center justify-end gap-3">
                    <button onClick={onClose} className="btn-outline-gold !text-xs" data-testid="modal-cancel">
                        İptal
                    </button>
                    <button
                        onClick={sendAndConfirm}
                        className="btn-gold !text-xs"
                        data-testid="modal-confirm-send"
                    >
                        <Send className="w-4 h-4" />
                        WhatsApp ile Gönder ve {isApprove ? "Onayla" : "Reddet"}
                    </button>
                </div>
            </div>
        </div>
    );
};

const AdminReservations = () => {
    const { t, settings, formatPrice } = useApp();
    const [searchParams] = useSearchParams();
    const [items, setItems] = useState([]);
    const [filter, setFilter] = useState("all");
    const [search, setSearch] = useState(searchParams.get("q") || "");
    const [modal, setModal] = useState(null);

    const load = () => api.get("/admin/reservations").then(({ data }) => setItems(data));
    useEffect(() => { load(); }, []);

    const confirmAction = async (payload) => {
        if (!modal) return;
        try {
            await api.patch(`/admin/reservations/${modal.reservation.id}`, payload);
            toast.success(payload.status === "approved" ? "Rezervasyon onaylandı" : "Rezervasyon reddedildi");
            setModal(null);
            load();
        } catch {
            toast.error("Güncellenemedi");
        }
    };

    const quickStatus = async (id, status) => {
        try {
            await api.patch(`/admin/reservations/${id}`, { status });
            toast.success("Durum güncellendi");
            load();
        } catch { toast.error("Hata"); }
    };

    const deleteReservation = async (r) => {
        if (!window.confirm(`${r.reservation_code} (${r.customer_name}) kalıcı olarak silinsin mi?`)) return;
        try {
            await api.delete(`/admin/reservations/${r.id}`);
            toast.success("Rezervasyon silindi");
            load();
        } catch { toast.error("Silinemedi"); }
    };

    const filtered = useMemo(() => {
        let list = filter === "all" ? items : items.filter((i) => i.status === filter);
        if (search.trim()) {
            const q = search.trim().toLowerCase();
            list = list.filter(
                (i) =>
                    (i.reservation_code || "").toLowerCase().includes(q) ||
                    (i.customer_name || "").toLowerCase().includes(q) ||
                    (i.customer_phone || "").toLowerCase().includes(q) ||
                    (i.hotel_name || "").toLowerCase().includes(q),
            );
        }
        return list;
    }, [items, filter, search]);

    const stats = useMemo(() => ({
        pending: items.filter((i) => i.status === "pending").length,
        approved: items.filter((i) => i.status === "approved").length,
        rejected: items.filter((i) => i.status === "rejected").length,
    }), [items]);

    const copy = (text) => {
        navigator.clipboard?.writeText(text);
        toast.success("Kopyalandı");
    };

    return (
        <div>
            <h1 className="font-serif text-4xl mb-2">{t("admin.reservations")}</h1>
            <p className="text-muted_green mb-6">Onaylama ve reddetme otomatik WhatsApp mesajı oluşturur</p>

            {/* Quick stats */}
            <div className="grid grid-cols-3 gap-4 mb-6">
                {[
                    { l: "Bekleyen", v: stats.pending, c: "text-yellow-400" },
                    { l: "Onaylanmış", v: stats.approved, c: "text-emerald-400" },
                    { l: "Reddedilen", v: stats.rejected, c: "text-red-400" },
                ].map((s) => (
                    <div key={s.l} className="border border-border_green bg-emerald_surface p-4">
                        <div className="text-[10px] uppercase tracking-[0.2em] text-muted_green">{s.l}</div>
                        <div className={`font-serif text-3xl ${s.c} mt-1`}>{s.v}</div>
                    </div>
                ))}
            </div>

            <div className="flex flex-wrap gap-3 mb-6 items-center">
                <div className="flex gap-2 flex-wrap">
                    {[
                        { v: "all", l: "Tümü" },
                        { v: "pending", l: "Bekleyen" },
                        { v: "approved", l: "Onaylanmış" },
                        { v: "rejected", l: "Reddedilen" },
                    ].map((f) => (
                        <button
                            key={f.v}
                            onClick={() => setFilter(f.v)}
                            className={`text-xs uppercase tracking-[0.15em] px-4 py-2 border font-semibold ${
                                filter === f.v ? "bg-gold text-emerald_deep border-gold" : "border-border_green text-bone hover:border-gold"
                            }`}
                            data-testid={`filter-${f.v}`}
                        >
                            {f.l}
                        </button>
                    ))}
                </div>
                <div className="flex-1 max-w-sm relative">
                    <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted_green" />
                    <input
                        type="text"
                        placeholder="ASS-XXXXXX, isim, telefon, otel…"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        className="input-dark pl-10 py-2"
                        data-testid="reservation-search"
                    />
                </div>
                <div className="text-xs text-muted_green ml-auto">{filtered.length} kayıt</div>
            </div>

            {/* Mobile card list (phone-friendly) */}
            <div className="md:hidden space-y-3" data-testid="reservations-mobile-list">
                {filtered.length === 0 && (
                    <div className="border border-border_green bg-emerald_surface p-8 text-center text-muted_green">Kayıt yok</div>
                )}
                {filtered.map((r) => (
                    <div key={r.id} className="border border-border_green bg-emerald_surface p-4" data-testid={`m-res-${r.id}`}>
                        <div className="flex items-center justify-between gap-2 mb-2">
                            <button onClick={() => copy(r.reservation_code)} className="font-mono text-gold text-sm inline-flex items-center gap-1" data-testid={`m-code-${r.id}`}>
                                {r.reservation_code || "—"} <Copy className="w-3 h-3" />
                            </button>
                            <span className={`text-[10px] uppercase tracking-[0.15em] px-2 py-1 border font-semibold ${
                                r.status === "pending" ? "text-yellow-400 border-yellow-400/40" :
                                r.status === "approved" ? "text-emerald-400 border-emerald-400/40" :
                                "text-red-400 border-red-400/40"
                            }`}>{r.status === "pending" ? "Beklemede" : r.status === "approved" ? "Onaylı" : "Reddedildi"}</span>
                        </div>
                        <div className="font-medium">{r.customer_name}</div>
                        <a href={`tel:${r.customer_phone}`} className="text-xs text-muted_green">{r.customer_phone}</a>
                        {r.company_name && <div className="text-xs text-gold">{r.company_name}</div>}
                        <div className="mt-2 text-sm">{r.hotel_name}</div>
                        <div className="text-xs text-muted_green">{r.room_name} × {r.rooms_count}</div>
                        <div className="mt-2 flex items-center justify-between gap-2 text-xs">
                            <div>
                                <div>{r.check_in} → {r.check_out}</div>
                                <div className="text-muted_green">{r.nights} gece · {r.guests} kişi · {r.customer_type === "tour" ? "Tur" : "Bireysel"}</div>
                                {(r.children > 0 || r.infants > 0) && (
                                    <div className="text-[10px] text-emerald-300 mt-0.5">
                                        {r.adults} yetişkin{r.children > 0 ? ` + ${r.children} çocuk` : ""}{r.infants > 0 ? ` + ${r.infants} bebek` : ""}
                                    </div>
                                )}
                            </div>
                            <div className="text-right">
                                <div className="font-serif text-gold text-xl whitespace-nowrap">{r.total_price} SAR</div>
                                {r.promo_code && <div className="text-[10px] text-emerald-300">🏷 {r.promo_code}</div>}
                            </div>
                        </div>
                        <div className="mt-3 pt-3 border-t border-border_green grid grid-cols-5 gap-2">
                            <button
                                onClick={() => setModal({ reservation: r, action: "approved" })}
                                disabled={r.status === "approved"}
                                className="py-2.5 border border-emerald-500/40 text-emerald-400 flex items-center justify-center disabled:opacity-25"
                                data-testid={`m-approve-${r.id}`}
                                aria-label="Onayla"
                            >
                                <Check className="w-4 h-4" />
                            </button>
                            <button
                                onClick={() => setModal({ reservation: r, action: "rejected" })}
                                disabled={r.status === "rejected"}
                                className="py-2.5 border border-red-500/40 text-red-400 flex items-center justify-center disabled:opacity-25"
                                data-testid={`m-reject-${r.id}`}
                                aria-label="Reddet"
                            >
                                <X className="w-4 h-4" />
                            </button>
                            <button
                                onClick={() => { const d = (r.customer_phone || "").replace(/\D/g, ""); window.open(`https://wa.me/${d}`, "_blank"); }}
                                className="py-2.5 border border-gold/40 text-gold flex items-center justify-center"
                                data-testid={`m-wa-${r.id}`}
                                aria-label="WhatsApp"
                            >
                                <MessageCircle className="w-4 h-4" />
                            </button>
                            <button
                                onClick={() => downloadReservationPdf({ reservation: r, settings, formatPrice })}
                                className="py-2.5 border border-gold/40 text-gold flex items-center justify-center"
                                data-testid={`m-pdf-${r.id}`}
                                aria-label="PDF"
                            >
                                <FileDown className="w-4 h-4" />
                            </button>
                            <button
                                onClick={() => deleteReservation(r)}
                                className="py-2.5 border border-red-500/40 text-red-400 flex items-center justify-center"
                                data-testid={`m-delete-${r.id}`}
                                aria-label="Sil"
                            >
                                <Trash2 className="w-4 h-4" />
                            </button>
                        </div>
                    </div>
                ))}
            </div>

            <div className="hidden md:block border border-border_green bg-emerald_surface overflow-x-auto">
                <table className="w-full text-sm min-w-[800px]">
                    <thead className="bg-emerald_deep">
                        <tr className="text-[10px] uppercase tracking-[0.15em] text-muted_green">
                            <th className="text-left p-4">Kod</th>
                            <th className="text-left p-4">Müşteri</th>
                            <th className="text-left p-4">Otel / Oda</th>
                            <th className="text-left p-4">Tarih</th>
                            <th className="text-left p-4">Tip</th>
                            <th className="text-right p-4">Toplam</th>
                            <th className="text-center p-4">Durum</th>
                            <th className="text-right p-4">İşlem</th>
                        </tr>
                    </thead>
                    <tbody>
                        {filtered.length === 0 && (
                            <tr><td colSpan={8} className="p-8 text-center text-muted_green">Kayıt yok</td></tr>
                        )}
                        {filtered.map((r) => (
                            <tr key={r.id} className="border-t border-border_green hover:bg-emerald_hover/40">
                                <td className="p-4">
                                    <button onClick={() => copy(r.reservation_code)} className="font-mono text-gold text-xs inline-flex items-center gap-1 hover:underline" data-testid={`code-${r.id}`}>
                                        {r.reservation_code || "—"} <Copy className="w-3 h-3" />
                                    </button>
                                </td>
                                <td className="p-4">
                                    <div className="font-medium">{r.customer_name}</div>
                                    <div className="text-xs text-muted_green">{r.customer_phone}</div>
                                    {r.company_name && <div className="text-xs text-gold">{r.company_name}</div>}
                                </td>
                                <td className="p-4">
                                    <div>{r.hotel_name}</div>
                                    <div className="text-xs text-muted_green">{r.room_name} × {r.rooms_count}</div>
                                </td>
                                <td className="p-4 text-xs">
                                    {r.check_in}<br />→ {r.check_out}<br />
                                    <span className="text-muted_green">{r.nights} g · {r.guests} kişi</span>
                                    {(r.children > 0 || r.infants > 0) && (
                                        <div className="text-[10px] text-emerald-300 mt-0.5">
                                            {r.adults} yetişkin{r.children > 0 ? ` + ${r.children} çocuk` : ""}{r.infants > 0 ? ` + ${r.infants} bebek` : ""}
                                        </div>
                                    )}
                                </td>
                                <td className="p-4 text-xs">
                                    <span className="text-[10px] uppercase tracking-[0.15em] text-gold">
                                        {r.customer_type === "tour" ? "Tur" : "Bireysel"}
                                    </span>
                                    <div className="text-muted_green mt-1">{r.meal_plan_label || r.meal_plan}</div>
                                </td>
                                <td className="p-4 text-right font-serif text-gold text-lg">
                                    {r.total_price} SAR
                                    {r.promo_code && (
                                        <div className="font-sans text-[10px] text-emerald-300 tracking-[0.1em] mt-0.5" title={`İndirim: ${r.promo_discount} SAR`}>
                                            🏷 {r.promo_code}
                                        </div>
                                    )}
                                </td>
                                <td className="p-4 text-center">
                                    <span className={`text-[10px] uppercase tracking-[0.2em] px-2 py-1 border font-semibold ${
                                        r.status === "pending" ? "text-yellow-400 border-yellow-400/40" :
                                        r.status === "approved" ? "text-emerald-400 border-emerald-400/40" :
                                        "text-red-400 border-red-400/40"
                                    }`}>{r.status === "pending" ? "Beklemede" : r.status === "approved" ? "Onaylı" : "Reddedildi"}</span>
                                </td>
                                <td className="p-4 text-right">
                                    <div className="flex justify-end gap-2">
                                        {r.status !== "approved" && (
                                            <button onClick={() => setModal({ reservation: r, action: "approved" })} title="Onayla + WhatsApp" className="p-2 border border-emerald-500/40 text-emerald-400 hover:bg-emerald-500/10" data-testid={`approve-${r.id}`}>
                                                <Check className="w-4 h-4" />
                                            </button>
                                        )}
                                        {r.status !== "rejected" && (
                                            <button onClick={() => setModal({ reservation: r, action: "rejected" })} title="Reddet + WhatsApp" className="p-2 border border-red-500/40 text-red-400 hover:bg-red-500/10" data-testid={`reject-${r.id}`}>
                                                <X className="w-4 h-4" />
                                            </button>
                                        )}
                                        <button
                                            onClick={() => {
                                                const phoneDigits = (r.customer_phone || "").replace(/\D/g, "");
                                                window.open(`https://wa.me/${phoneDigits}`, "_blank");
                                            }}
                                            title="WhatsApp aç"
                                            className="p-2 border border-gold/40 text-gold hover:bg-gold/10"
                                            data-testid={`wa-${r.id}`}
                                        >
                                            <MessageCircle className="w-4 h-4" />
                                        </button>
                                        <button
                                            onClick={() => downloadReservationPdf({ reservation: r, settings, formatPrice })}
                                            title="PDF Makbuz İndir"
                                            className="p-2 border border-gold/40 text-gold hover:bg-gold/10"
                                            data-testid={`pdf-${r.id}`}
                                        >
                                            <FileDown className="w-4 h-4" />
                                        </button>
                                        <button
                                            onClick={() => deleteReservation(r)}
                                            title="Rezervasyonu Sil"
                                            className="p-2 border border-red-500/40 text-red-400 hover:bg-red-500/10"
                                            data-testid={`delete-${r.id}`}
                                        >
                                            <Trash2 className="w-4 h-4" />
                                        </button>
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {modal && (
                <ActionModal
                    reservation={modal.reservation}
                    action={modal.action}
                    settings={settings}
                    onClose={() => setModal(null)}
                    onConfirm={confirmAction}
                />
            )}
        </div>
    );
};

export default AdminReservations;
